import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionTag } from "./Reveal";
import { Field, FilePicker, inputCls } from "./QuoteSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS = ["Jet A-1", "AGO / Diesel", "PMS", "Marine Fuels", "Fuel Oil", "Lubricants", "Other Petroleum Product"];
const UNITS = ["Litres", "Metric Tonnes", "Barrels", "US Gallons"];

const INITIAL = {
  product: "", quantity: "", unit: "Litres", delivery_location: "", required_date: "",
  current_supplier: "", current_price: "", company_name: "", contact_name: "", email: "", phone: "", notes: "",
};

export default function MarketCheckSection() {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [referenceId, setReferenceId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("request_type", "market_check");
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const res = await axios.post(`${API}/quote-requests`, fd);
      setReferenceId(res.data.reference_id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Submission failed", { description: typeof detail === "string" ? detail : "Please check your details and try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="market-check" data-testid="market-check-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(217,164,65,0.07),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <div className="lg:sticky lg:top-32">
            <SectionTag label="Ajemi Market Check" />
            <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white">
              Test the market <span className="text-gold">before you commit.</span>
            </h2>
            <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
              About to buy? Let Ajemi check the market first. We obtain available supplier
              indications and compare suitable options — so you commit with greater market
              visibility.
            </p>
            <div className="mt-10 space-y-5">
              {[
                "Benchmark your next procurement against available supply options",
                "See who actually has supply available in your location",
                "Compare price, delivery and payment terms side by side",
                "Keep your current supplier — use Ajemi as your market check",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="font-outfit text-gold text-sm mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm text-white/60 leading-relaxed">{t}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-xs text-white/35 leading-relaxed max-w-md">
              A market check creates visibility, not obligation. No savings are guaranteed —
              you always make the final purchasing decision.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="glass-panel rounded-2xl p-7 md:p-10" data-testid="market-check-panel">
            {referenceId ? (
              <div className="text-center py-12" data-testid="market-check-success">
                <CheckCircle2 className="w-14 h-14 text-gold mx-auto" strokeWidth={1.2} />
                <h3 className="font-outfit text-3xl font-light text-white mt-6">Market check started.</h3>
                <p className="text-white/60 mt-4 max-w-md mx-auto leading-relaxed">
                  Ajemi is checking available supply options for your requirement. Our team will
                  contact you with what the market shows.
                </p>
                <p className="mt-6 inline-block border border-gold/40 text-gold font-outfit tracking-[0.2em] text-sm rounded-full px-6 py-2.5" data-testid="market-check-reference-id">
                  REF · {referenceId}
                </p>
                <div className="mt-10">
                  <button
                    data-testid="market-check-another-btn"
                    onClick={() => { setReferenceId(null); setForm(INITIAL); setFiles([]); }}
                    className="text-sm text-white/50 hover:text-gold transition-colors underline underline-offset-4"
                  >
                    Start another market check
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="market-check-form">
                <Field label="Product" required testId="mc-field-product">
                  <select required value={form.product} onChange={set("product")} className={inputCls} data-testid="mc-product-select">
                    <option value="" disabled>Select a product</option>
                    {PRODUCTS.map((p) => <option key={p} value={p} className="bg-surface">{p}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Quantity" required testId="mc-field-quantity">
                    <input required type="number" min="1" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g. 500,000" data-testid="mc-quantity-input" />
                  </Field>
                  <Field label="Unit" testId="mc-field-unit">
                    <select value={form.unit} onChange={set("unit")} className={inputCls} data-testid="mc-unit-select">
                      {UNITS.map((u) => <option key={u} value={u} className="bg-surface">{u}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Location" required testId="mc-field-location">
                  <input required value={form.delivery_location} onChange={set("delivery_location")} className={inputCls} placeholder="e.g. Lagos" data-testid="mc-location-input" />
                </Field>
                <Field label="Required Date" testId="mc-field-date">
                  <input type="date" value={form.required_date} onChange={set("required_date")} className={inputCls} data-testid="mc-date-input" />
                </Field>
                <Field label="Current Supplier (optional)" testId="mc-field-current-supplier">
                  <input value={form.current_supplier} onChange={set("current_supplier")} className={inputCls} placeholder="Who supplies you today" data-testid="mc-current-supplier-input" />
                </Field>
                <Field label="Current Quoted Price (optional)" testId="mc-field-current-price">
                  <input value={form.current_price} onChange={set("current_price")} className={inputCls} placeholder="e.g. ₦1,720 / litre" data-testid="mc-current-price-input" />
                </Field>
                <Field label="Company Name" required testId="mc-field-company">
                  <input required value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="Your company" data-testid="mc-company-input" />
                </Field>
                <Field label="Contact Name" required testId="mc-field-contact">
                  <input required value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Full name" data-testid="mc-contact-input" />
                </Field>
                <Field label="Email" required testId="mc-field-email">
                  <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="name@company.com" data-testid="mc-email-input" />
                </Field>
                <Field label="Phone" testId="mc-field-phone">
                  <input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+234 ..." data-testid="mc-phone-input" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Anything Else" testId="mc-field-notes">
                    <textarea rows={3} value={form.notes} onChange={set("notes")} className={`${inputCls} resize-none`} placeholder="Specifications, contract context, delivery requirements…" data-testid="mc-notes-input" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <FilePicker files={files} setFiles={setFiles} testId="mc-file-upload" />
                </div>
                <div className="sm:col-span-2 pt-1">
                  <button type="submit" disabled={loading} className="group w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full px-8 py-4 text-base hover:bg-goldbright hover:shadow-[0_0_44px_rgba(217,164,65,0.5)] transition-all duration-300 disabled:opacity-60" data-testid="mc-submit-btn">
                    {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</>) : (<>Start Market Check<ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
